import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Heading, 
  Text, 
  Divider,
  Icon,
  Flex,
  chakra,
  Grid,
  GridItem,
  Badge,
  HStack,
  Image,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton
} from '@chakra-ui/react';
import { motion, useAnimation } from 'framer-motion';
import { FaCogs, FaLeaf, FaMobileAlt, FaWifi, FaShieldAlt, FaArrowLeft, FaArrowRight, FaExpand, FaSearchPlus, FaSearchMinus } from 'react-icons/fa';
import { texts } from '../lang';
import { useLanguage } from '../context/LanguageContext';
import { COLORS } from '../globalvariables';
import { AnimatePresence } from 'framer-motion';
import { LANGUAGE_CHANGE_EVENT } from '../context/LanguageContext';

// Import our components
import FeatureShowcase from '../components/Machine/FeatureShowcase';
import MachineModelCard from '../components/Machine/MachineModelCard';
import MachineHeader from '../components/Machine/MachineHeader';

const MotionHeading = chakra(motion.h2);
const MotionBox = chakra(motion.div);
const MotionText = chakra(motion.p);
const MotionFlex = chakra(motion.div);
const MotionIcon = chakra(motion.div);
const MotionImage = chakra(motion.img);

const Machine = () => {
  const { language } = useLanguage();
  const machineTexts = texts[language].machine;
  const machineImages = texts[language].images.machines;

  const controls = useAnimation();
  
  // State for image gallery functionality
  const [eliteCurrentIndex, setEliteCurrentIndex] = useState(0);
  const [miniCurrentIndex, setMiniCurrentIndex] = useState(0);
  const [activeGallery, setActiveGallery] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  useEffect(() => {
    // Start animation when component mounts
    controls.start({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, delay: 0.2 }
    });
    
    // Scroll to top when component mounts (only if no hash)
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    } else {
      // If there's a hash, scroll to the element after a small delay to ensure render
      setTimeout(() => {
        const targetId = window.location.hash.substring(1); // Remove the # character
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          // Get the element's position
          const rect = targetElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          
          // Calculate a position that's 100px above the element
          const scrollToY = scrollTop + rect.top - 150;
          
          // Scroll to the adjusted position
          window.scrollTo({
            top: scrollToY,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  }, [controls]);

  // Écouter les changements de langue pour fermer les modales
  useEffect(() => {
    const handleLanguageChange = () => {
      // Fermer la galerie si elle est ouverte
      if (isOpen) {
        onClose();
      }
    };
    
    // Ajouter l'écouteur d'événement
    document.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    
    // Nettoyage
    return () => {
      document.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, [isOpen, onClose]);

  // Add effect to prevent body scroll when gallery is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
    };
  }, [isOpen]);

  // The names for the machine models
  const proModel = {
    name: machineTexts.modelElite.name,
    nameAlt: machineTexts.modelElite.nameAlt,
    isPremium: machineTexts.modelElite.isPremium
  };
  
  const compactModel = {
    name: machineTexts.modelMini.name,
    nameAlt: machineTexts.modelMini.nameAlt,
    isPremium: machineTexts.modelMini.isPremium
  };

  // Prepare the technical specifications for the Elite model
  const eliteSpecs = {
    highlights: machineTexts.modelElite.highlights,
    categories: [
      {
        name: machineTexts.modelElite.specs.technical.name,
        items: machineTexts.modelElite.specs.technical.items
      },
      {
        name: machineTexts.modelElite.specs.dimensions.name,
        items: machineTexts.modelElite.specs.dimensions.items
      },
      {
        name: machineTexts.modelElite.specs.performance.name,
        items: machineTexts.modelElite.specs.performance.items
      },
      {
        name: machineTexts.modelElite.specs.smart.name,
        items: machineTexts.modelElite.specs.smart.items
      },
      {
        name: machineTexts.modelElite.specs.service.name,
        items: machineTexts.modelElite.specs.service.items
      }
    ]
  };

  // Prepare the technical specifications for the Mini model
  const miniSpecs = {
    highlights: machineTexts.modelMini.highlights,
    categories: [
      {
        name: machineTexts.modelMini.specs.technical.name,
        items: machineTexts.modelMini.specs.technical.items
      },
      {
        name: machineTexts.modelMini.specs.dimensions.name,
        items: machineTexts.modelMini.specs.dimensions.items
      },
      {
        name: machineTexts.modelMini.specs.performance.name,
        items: machineTexts.modelMini.specs.performance.items
      },
      {
        name: machineTexts.modelMini.specs.smart.name,
        items: machineTexts.modelMini.specs.smart.items
      }
    ]
  };

  // Elite model images
  const eliteImages = machineImages.elite.gallery;

  // Mini model images
  const miniImages = machineImages.mini.gallery;

  // Key features to showcase from language file
  const keyFeatures = machineTexts.features;

  // Gallery functionality
  const openGallery = (gallery, index = 0) => {
    setActiveGallery(gallery);
    setActiveIndex(index);
    resetZoom();
    onOpen();
  };

  const handlePrevious = (e) => {
    e.preventDefault();
    if (activeGallery === 'elite') {
      setEliteCurrentIndex(prev => prev === 0 ? eliteImages.length - 1 : prev - 1);
    } else {
      setMiniCurrentIndex(prev => prev === 0 ? miniImages.length - 1 : prev - 1);
    }
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (activeGallery === 'elite') {
      setEliteCurrentIndex(prev => prev === eliteImages.length - 1 ? 0 : prev + 1);
    } else {
      setMiniCurrentIndex(prev => prev === miniImages.length - 1 ? 0 : prev + 1);
    }
  };

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setScale(prev => {
      const newScale = Math.max(prev - 0.5, 1);
      if (newScale === 1) resetZoom();
      return newScale;
    });
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleSelectThumbnail = (index) => {
    if (activeGallery === 'elite') {
      setEliteCurrentIndex(index);
    } else {
      setMiniCurrentIndex(index);
    }
  };

  return (
    <Box 
      as="main" 
      minH="100vh"
      bg={COLORS.background}
      color={COLORS.textPrimary}
      w="100vw"
      maxW="100%"
      overflowX="hidden"
      margin="0"
      padding="0"
      style={{ overflow: isOpen ? 'hidden' : 'auto' }}
    >
      {/* Use the MachineHeader component */}
      <MachineHeader machineTexts={machineTexts} />

      {/* Key features showcase */}
      <FeatureShowcase 
        title={machineTexts.featuresTitle}
        description={machineTexts.modelsDescription}
        features={keyFeatures}
      />

      {/* Models section header */}
      <Box py={{ base: 8, md: 16 }} textAlign="center">
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={controls}
          maxW="800px"
          mx="auto"
          px={{ base: 2, md: 4 }}
        >
          <Flex justify="center" align="center" mb={{ base: 3, md: 4 }}>
            <Icon as={FaCogs} color={COLORS.primary} boxSize={{ base: "24px", md: "30px" }} mr={{ base: 2, md: 3 }} />
            <MotionHeading
              as="h2"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              bgGradient={`linear(to-r, ${COLORS.textPrimary}, ${COLORS.primary} 50%, ${COLORS.textPrimary})`}
              bgClip="text"
            >
              {machineTexts.modelsTitle}
            </MotionHeading>
          </Flex>
          
          <Text
            fontSize={{ base: "md", md: "xl" }}
            color={COLORS.textPrimary}
            maxW="700px"
            mx="auto"
          >
            {machineTexts.modelsDescription}
          </Text>
        </MotionBox>
      </Box>

      {/* Elite model with integrated gallery */}
      <Container maxW="1200px" mb={{ base: 8, md: 16 }} px={{ base: 2, md: 4 }}>
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <MachineModelCard
            title={proModel.name}
            description={machineTexts.modelElite.description}
            image={eliteImages[eliteCurrentIndex]}
            specs={eliteSpecs}
            isPremium={true}
            animationDelay={0.2}
            hasGallery={true}
            galleryImages={eliteImages}
            currentIndex={eliteCurrentIndex}
            setCurrentIndex={setEliteCurrentIndex}
            onOpenGallery={() => openGallery('elite', eliteCurrentIndex)}
            modelId={1}
          />
        </MotionBox>
      </Container>

      <Divider w="80%" mx="auto" my={{ base: 8, md: 16 }} opacity={0.3} />

      {/* Mini model with integrated gallery */}
      <Container maxW="1200px" mb={{ base: 8, md: 16 }} px={{ base: 2, md: 4 }}>
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <MachineModelCard
            title={compactModel.name}
            description={machineTexts.modelMini.description}
            image={miniImages[miniCurrentIndex]}
            specs={miniSpecs}
            isPremium={false}
            animationDelay={0.4}
            hasGallery={true}
            galleryImages={miniImages}
            currentIndex={miniCurrentIndex}
            setCurrentIndex={setMiniCurrentIndex}
            onOpenGallery={() => openGallery('mini', miniCurrentIndex)}
            modelId={2}
          />
        </MotionBox>
      </Container>
      
      {/* Fullscreen Modal for Image Gallery */}
      <Modal
        isOpen={isOpen}
        onClose={() => {
          resetZoom();
          onClose();
        }}
        size="full"
      >
        <ModalOverlay bg="rgba(0, 0, 0, 0.95)" />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton 
            color="white" 
            size="lg" 
            zIndex={20}
            top={{ base: "20px", md: "30px" }}
            right={{ base: "20px", md: "30px" }}
            w={{ base: "40px", md: "50px" }}
            h={{ base: "40px", md: "50px" }}
            fontSize={{ base: "18px", md: "20px" }}
            bg="rgba(255, 255, 255, 0.2)"
            _hover={{ bg: "rgba(255, 255, 255, 0.3)" }}
            borderRadius="full"
            border="2px solid rgba(255, 255, 255, 0.3)"
          />
          <ModalBody
            p={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            h="100vh"
            style={{ overflow: 'hidden' }}
          >
            <MotionBox
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              position="relative"
              h="100%"
              w="100%" 
              display="flex"
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
            >
              <Flex position="relative" w="100%" h="100%" justify="center" align="center">
                {/* Thumbnails sur le côté gauche */}
                <Flex 
                  direction="column"
                  position="absolute"
                  left={{ base: "10px", md: "40px" }}
                  top="50%"
                  transform="translateY(-50%)"
                  p={3}
                  borderRadius="md"
                  maxH="400px"
                  overflowY="auto"
                  zIndex={10}
                  display={{ base: 'none', md: 'flex' }}
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: COLORS.primary,
                      borderRadius: '3px',
                    },
                  }}
                >
                  {(activeGallery === 'elite' ? eliteImages : miniImages).map((image, index) => (
                    <Box 
                      key={index} 
                      cursor="pointer"
                      borderWidth="2px"
                      borderColor={index === (activeGallery === 'elite' ? eliteCurrentIndex : miniCurrentIndex) 
                        ? COLORS.primary 
                        : "transparent"
                      }
                      borderRadius="md"
                      overflow="hidden"
                      onClick={() => handleSelectThumbnail(index)}
                      transition="all 0.2s"
                      opacity={index === (activeGallery === 'elite' ? eliteCurrentIndex : miniCurrentIndex) ? 1 : 0.6}
                      _hover={{ opacity: 0.9 }}
                      mb={2}
                      flexShrink={0}
                    >
                      <Image 
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        w="70px"
                        h="50px"
                        objectFit="cover"
                      />
                    </Box>
                  ))}
                </Flex>

                {/* Image principale avec zoom */}
                <MotionBox
                  drag={scale > 1}
                  dragConstraints={{
                    left: -200 * (scale - 1),
                    right: 200 * (scale - 1),
                    top: -200 * (scale - 1),
                    bottom: 200 * (scale - 1)
                  }}
                  dragElastic={0.1}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  style={{
                    scale,
                    x: position.x,
                    y: position.y,
                    cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default"
                  }}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  w="100%"
                  h="100%"
                >
                  <Box
                    width={{ base: "95%", md: "90%" }}
                    height={{ base: "90vh", md: "90vh" }}
                    position="relative"
                    display="flex" 
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Image
                      src={activeGallery === 'elite' 
                        ? eliteImages[eliteCurrentIndex] 
                        : miniImages[miniCurrentIndex]
                      }
                      alt={activeGallery === 'elite' ? "1000Shake Elite" : "1000Shake Compact"}
                      maxH="100%"
                      maxW="100%"
                      objectFit="contain"
                      draggable="false"
                    />
                  </Box>
                </MotionBox>

                {/* Fullscreen navigation */}
                <Flex
                  position="absolute"
                  top="0"
                  bottom="0"
                  left="0"
                  right="0"
                  align="center"
                  justify="space-between"
                  px={{ base: 4, md: 8 }}
                  pointerEvents="none"
                  maxW={{ base: "100%", md: "1200px" }}
                  mx="auto"
                >
                  <IconButton
                    icon={<FaArrowLeft />}
                    aria-label={machineTexts.gallery.previous}
                    onClick={handlePrevious}
                    variant="solid"
                    bg="rgba(255, 255, 255, 0.2)"
                    color="white"
                    size="lg"
                    pointerEvents="auto"
                    opacity={0.7}
                    _hover={{ opacity: 1, bg: "rgba(255, 255, 255, 0.3)" }}
                    borderRadius="full"
                    w="50px"
                    h="50px"
                  />
                  <IconButton
                    icon={<FaArrowRight />}
                    aria-label={machineTexts.gallery.next}
                    onClick={handleNext}
                    variant="solid"
                    bg="rgba(255, 255, 255, 0.2)"
                    color="white"
                    size="lg"
                    pointerEvents="auto"
                    opacity={0.7}
                    _hover={{ opacity: 1, bg: "rgba(255, 255, 255, 0.3)" }}
                    borderRadius="full"
                    w="50px"
                    h="50px"
                  />
                </Flex>
              </Flex>
            </MotionBox>
          </ModalBody>
        </ModalContent>
      </Modal>
      
      {/* Spacer at bottom */}
      <Box h={20} />
    </Box>
  );
};

export default Machine;